// Pure helpers for the Look Editor's text-field sizing rules. Extracted from
// LookPreviewCanvas so the wrap-vs-single-line decision is unit-testable
// (LookPreviewCanvas itself needs a real <canvas> to render).
//
// The rules mirror EC2's processors/pose_renderer.py by name:
//   - 'wrap' -> word-wrap greedy to fit the field width
//   - 'single_line_or_wrap' -> one line if the whole content fits, else wrap
//   - anything else (blank / 'fit_to_bounds_max_first' / 'fixed' / …) -> one line
// The RENDERED font size is chosen elsewhere (EC2 binary-searches [min,max];
// the preview uses one fs); this helper only decides whether to wrap.

/**
 * Return the array of lines a text field should render given its sizing rule.
 *
 * `measureWidth(text)` and `wrap(text, maxWidth)` are injected so this stays
 * pure and testable without a real canvas. Callers in LookPreviewCanvas.jsx
 * pass `(t) => ctx.measureText(t).width` and the greedy wrap helper.
 *
 * `content` may be null/undefined; treated as ''.
 */
export function computeTextLines(sizingRule, content, maxWidth, measureWidth, wrap) {
  const text = content || '';

  if (sizingRule === 'wrap') return wrap(text, maxWidth);

  if (sizingRule === 'single_line_or_wrap') {
    // Wrap ONLY when the whole content is genuinely too wide for one line.
    return measureWidth(text) <= maxWidth ? [text] : wrap(text, maxWidth);
  }

  // 'fixed' / 'shrink_to_fit' / 'fit_to_bounds_max_first' / '' / undefined
  // -> always single line (matches EC2's non-wrap branches).
  return [text];
}

/**
 * Baseline-to-baseline line-height multiplier (applied to font size) when a
 * text field wraps in the preview. Explicit per-field override wins;
 * otherwise falls back to the rule-based default (mirrors EC2
 * pose_renderer.py):
 *   - single_line_or_wrap -> 0.9 (aggressive-tight display type)
 *   - every other rule    -> 1.25 (preview's historical body-text default)
 * Bad values (null, undefined, non-numeric, non-positive) fall through to
 * the rule default so a blank input never renders overlapped or invisible.
 */
export function getLineHeightMultiplier(sizingRule, explicitMultiplier) {
  const parsed = parseFloat(explicitMultiplier);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return sizingRule === 'single_line_or_wrap' ? 0.9 : 1.25;
}
